import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ErrorNote } from "@/pages/Login";

const TARGET_ROLES = [
  ["devops_engineer", "DevOps Engineer", "Pipelines, containers, infrastructure automation"],
  ["cloud_engineer", "Cloud Engineer", "AWS architecture, networking, IAM, cost"],
  ["platform_engineer", "Platform Engineer", "Internal platforms, developer experience, tooling"],
  ["sre", "Site Reliability Engineer", "SLOs, observability, incident response"],
];

const STYLES = [
  ["hands_on", "Hands-on first", "Give me the exercise, then explain"],
  ["concept_first", "Concept first", "Explain the model, then let me build"],
  ["reference", "Reference-heavy", "Show production examples I can adapt"],
];

const STEPS = ["Your background", "Target role", "Time commitment", "Learning style"];

export default function Onboarding() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    currentRole: "",
    yearsExperience: 3,
    targetRole: "devops_engineer",
    weeklyStudyHours: 8,
    timelineWeeks: 12,
    learningStyle: "hands_on",
    primaryCloud: "aws",
    motivation: "",
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      await api.put("/profile", {
        ...form,
        yearsExperience: Number(form.yearsExperience),
        weeklyStudyHours: Number(form.weeklyStudyHours),
        timelineWeeks: Number(form.timelineWeeks),
      });
      await refresh();
      navigate("/assessment", { replace: true });
    } catch (err) {
      setError(errMsg(err, "Unable to save profile"));
    } finally {
      setBusy(false);
    }
  };

  const canNext = step === 0 ? form.currentRole.trim().length > 1 : true;

  return (
    <div className="min-h-screen">
      <div className="border-b border-border">
        <div className="max-w-3xl mx-auto px-6 py-6">
          <p className="overline text-primary">onboarding · step {step + 1} of {STEPS.length}</p>
          <h1 className="mt-3 text-2xl sm:text-3xl font-display font-bold tracking-tight">{STEPS[step]}</h1>
          <div className="mt-6 flex gap-1" data-testid="onboarding-progress">
            {STEPS.map((s, i) => (
              <div key={s} className={`h-1 flex-1 ${i <= step ? "bg-primary" : "bg-border"}`} />
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-10 fade-up" data-testid="onboarding-form">
        <ErrorNote message={error} />

        {step === 0 && (
          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="currentRole">Current role</Label>
              <Input id="currentRole" data-testid="onboarding-current-role" className="rounded-none" placeholder="Backend Engineer" value={form.currentRole} onChange={(e) => set("currentRole", e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="years">Years of engineering experience: {form.yearsExperience}</Label>
              <input id="years" data-testid="onboarding-years" type="range" min="0" max="20" value={form.yearsExperience} onChange={(e) => set("yearsExperience", e.target.value)} className="w-full accent-[hsl(var(--primary))]" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="motivation">Why now? (optional)</Label>
              <Textarea id="motivation" data-testid="onboarding-motivation" className="rounded-none" rows={3} placeholder="I own deploys for my team and want to do it properly." value={form.motivation} onChange={(e) => set("motivation", e.target.value)} />
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-border border border-border">
            {TARGET_ROLES.map(([slug, name, desc]) => (
              <button
                key={slug}
                data-testid={`onboarding-role-${slug}`}
                onClick={() => set("targetRole", slug)}
                className={`text-left bg-background p-6 transition-colors duration-150 ${form.targetRole === slug ? "ring-1 ring-inset ring-primary" : "hover:bg-accent/50"}`}
              >
                <p className="font-display font-bold tracking-tight">{name}</p>
                <p className="mt-1 text-sm text-muted-foreground">{desc}</p>
              </button>
            ))}
          </div>
        )}

        {step === 2 && (
          <div className="space-y-8">
            <div className="space-y-2">
              <Label>Weekly study hours: {form.weeklyStudyHours}h</Label>
              <input data-testid="onboarding-hours" type="range" min="2" max="30" value={form.weeklyStudyHours} onChange={(e) => set("weeklyStudyHours", e.target.value)} className="w-full accent-[hsl(var(--primary))]" />
              <p className="font-mono text-xs text-muted-foreground">Roadmap pacing is calculated from this number.</p>
            </div>
            <div className="space-y-2">
              <Label>Target timeline: {form.timelineWeeks} weeks</Label>
              <input data-testid="onboarding-timeline" type="range" min="4" max="52" value={form.timelineWeeks} onChange={(e) => set("timelineWeeks", e.target.value)} className="w-full accent-[hsl(var(--primary))]" />
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="grid grid-cols-1 gap-px bg-border border border-border">
            {STYLES.map(([slug, name, desc]) => (
              <button
                key={slug}
                data-testid={`onboarding-style-${slug}`}
                onClick={() => set("learningStyle", slug)}
                className={`text-left bg-background p-6 transition-colors duration-150 ${form.learningStyle === slug ? "ring-1 ring-inset ring-primary" : "hover:bg-accent/50"}`}
              >
                <p className="font-display font-bold tracking-tight">{name}</p>
                <p className="mt-1 text-sm text-muted-foreground">{desc}</p>
              </button>
            ))}
          </div>
        )}

        <div className="mt-10 flex justify-between">
          <Button variant="ghost" className="rounded-none gap-2" disabled={step === 0} onClick={() => setStep(step - 1)} data-testid="onboarding-back">
            <ArrowLeft className="h-4 w-4" /> Back
          </Button>
          {step < STEPS.length - 1 ? (
            <Button className="rounded-none gap-2" disabled={!canNext} onClick={() => setStep(step + 1)} data-testid="onboarding-next">
              Continue <ArrowRight className="h-4 w-4" />
            </Button>
          ) : (
            <Button className="rounded-none gap-2" disabled={busy} onClick={submit} data-testid="onboarding-submit">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save &amp; start assessment
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
